// The phone sheets (see sheets.ts). Anchored to the bottom of the visual viewport, so the
// on-screen keyboard never covers them.

import { useEffect, useRef, useState } from 'react';
import { IconArrowUpRight, IconCornerDownLeft } from '@tabler/icons-react';
import { toast } from '../store';
import { Button, Ic, usePresence } from './ds';
import { peekTerm } from '../term/manager';
import { closeSheet, copyText, useSheet, type Sheet } from './sheets';
import { desktopKeys, desktopPaste } from './desktopLink';
import { escCloses } from './keys';
import { openClicked } from './openLinkStore';

function backToTerminal(sid: number | null) {
  closeSheet();
  // Refocus so the keyboard and key bar come back where the user was typing.
  if (sid !== null) requestAnimationFrame(() => peekTerm(sid)?.focus());
}

function Compose({ sid }: { sid: number | null }) {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const insert = (enter: boolean) => {
    if (sid === null) {
      // Desktop: paste into the focused Linux app, then Enter after the paste has landed.
      if (text) desktopPaste(text);
      if (enter) setTimeout(() => desktopKeys([{ key: 'Enter', code: 'Enter' }]), 500);
    } else {
      const t = peekTerm(sid);
      if (t && text) t.paste(text);
      if (t && enter) t.input('\r');
    }
    backToTerminal(sid);
  };
  return (
    <>
      <div className="ws-sheet-head">
        <span className="ws-sheet-title">Paste or type</span>
        <button className="ws-sheet-cancel" onClick={() => backToTerminal(sid)} data-testid="sheet-cancel">
          Cancel
        </button>
      </div>
      <textarea
        ref={ref}
        className="ws-sheet-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Long-press here and choose Paste"
        autoCapitalize="off"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        data-testid="compose-text"
      />
      <div className="ws-sheet-actions">
        <Button className="ws-sheet-btn" disabled={!text} onClick={() => insert(false)} data-testid="compose-insert">
          Insert
        </Button>
        <Button variant="primary" className="ws-sheet-btn" disabled={!text} onClick={() => insert(true)} data-testid="compose-send">
          Insert
          <Ic icon={IconCornerDownLeft} stroke={1.8} />
        </Button>
      </div>
    </>
  );
}

function Select({ sid, text }: { sid: number; text: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const ta = ref.current;
    if (ta) ta.scrollTop = ta.scrollHeight; // start at the latest output, like the terminal
  }, []);
  const copyAll = async () => {
    const ok = await copyText(text);
    toast({ title: ok ? 'Copied' : 'Copy failed', body: ok ? undefined : 'Select the text and use Copy instead.', kind: ok ? undefined : 'error' }, 2000);
  };
  return (
    <>
      <div className="ws-sheet-head">
        <span className="ws-sheet-title">Select text</span>
        <button className="ws-sheet-cancel" onClick={() => backToTerminal(sid)} data-testid="sheet-cancel">
          Done
        </button>
      </div>
      {/* readOnly keeps the keyboard down while native selection (handles, Copy) still works. */}
      <textarea ref={ref} className="ws-sheet-text ws-sheet-select" value={text} readOnly wrap="soft" data-testid="select-text" />
      <div className="ws-sheet-actions">
        <span className="ws-sheet-hint">Long-press to select, then Copy</span>
        <Button variant="primary" className="ws-sheet-btn" onClick={() => void copyAll()} data-testid="select-copy-all">
          Copy all
        </Button>
      </div>
    </>
  );
}

function LinkMenu({ uri, sid }: { uri: string; sid?: number }) {
  const copy = async () => {
    const ok = await copyText(uri);
    toast({ title: ok ? 'Link copied' : 'Copy failed', kind: ok ? undefined : 'error' }, 2000);
    closeSheet();
  };
  return (
    <>
      <div className="ws-sheet-head">
        <span className="ws-sheet-title">Link</span>
        <button className="ws-sheet-cancel" onClick={closeSheet} data-testid="sheet-cancel">
          Cancel
        </button>
      </div>
      <div className="ws-sheet-uri" data-testid="link-uri">
        {uri}
      </div>
      <div className="ws-sheet-actions">
        <Button className="ws-sheet-btn" onClick={() => void copy()} data-testid="link-copy">
          Copy
        </Button>
        <a
          className="ds-btn ws-sheet-btn"
          data-variant="primary"
          href={uri}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            // from a session: where a program there opening it would (openClicked)
            if (sid !== undefined) {
              e.preventDefault();
              openClicked(sid, uri);
            }
            closeSheet();
          }}
          data-testid="link-open"
        >
          Open
          <Ic icon={IconArrowUpRight} stroke={1.8} />
        </a>
      </div>
    </>
  );
}

function body(s: Sheet) {
  switch (s.kind) {
    case 'compose':
      return <Compose sid={s.sid} />;
    case 'select':
      return <Select sid={s.sid} text={s.text} />;
    case 'link':
      return <LinkMenu uri={s.uri} sid={s.sid} />;
  }
}

export function TermSheet() {
  const s = useSheet();
  // the sheet's last content stays while it slides away
  const last = useRef(s);
  if (s) last.current = s;
  const { mounted, shown } = usePresence(!!s, 180);
  useEffect(() => {
    if (!s) return;
    // Capture: the terminal under the sheet keeps focus (the link menu) and would take Esc.
    const onKey = escCloses(closeSheet);
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [s]);
  const cur = s ?? last.current;
  if (!mounted || !cur) return null;
  return (
    <div className="ws-sheet-backdrop" data-shown={shown ? '' : undefined} onClick={closeSheet} data-testid="term-sheet" data-kind={cur.kind}>
      <div className="ws-sheet" role="dialog" aria-modal="true" aria-label={cur.kind === 'compose' ? 'Paste or type' : cur.kind} onClick={(e) => e.stopPropagation()}>
        <span className="ws-sheet-grab" aria-hidden="true" />
        {body(cur)}
      </div>
    </div>
  );
}
